<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import store from "@/store";

import { maskCurrency, maskCurrencyToFloat } from "@/common/utils/mask";
import { toast } from "@/common/utils/toast";

import { planList, planCreate, planDelete } from "@/common/api/rest/plan/plan";

import type { PlanType, PlanPayloadType } from "@/common/types/plan/plan";

import { PanelTheme } from "@/common/themes";
import { FormPlan } from "@/components";
import {
	breadcrumbSimple,
	baseConfirm,
	baseModal,
	baseTable,
	svgIcon,
} from "@/common/components";

const { t, tm } = useI18n();
const router = useRouter();

const tableData = ref<PlanType[]>([]);
const payload = ref<PlanPayloadType | null>(null);

const selected = ref<PlanType | null>(null);

const limit = ref<number>(6);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);
const loading = ref(false);

const tableHeader = computed(() => [
	{ key: "name", label: t("common.labels.name") },
	{ key: "status", label: t("common.labels.status") },
	{ key: "free", label: t("common.labels.free") },
]);

const breadcrumbList = computed(() => [
	{
		key: "register",
		label: t("common.labels.registrations"),
		url: "/register",
	},
	{
		key: "plans",
		label: t("common.labels.plans"),
		url: "/register/plan",
	},
]);

const statusOptions = computed(() => ({
	ACTIVE: t("common.labels.active"),
	INACTIVE: t("common.labels.inactive"),
}));

const currencies = computed(
	() => tm("enums.currencies") as Record<string, string>,
);

function fetchData() {
	loading.value = true;
	planList({
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;
			if (success) {
				tableData.value = data.list?.map((item: PlanType) => {
					// const newPrice = item.price ? item.price / 100 : 0;
					return {
						...item,
						// price: newPrice,
					};
				});
				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			loading.value = false;
		});
}

function changePage(value: number) {
	page.value = value;
	fetchData();
}

function searchItem(value: string) {
	search.value = value;

	fetchData();
}

function addItem() {
	showModalAdd.value = true;
}

function addItemCancel() {
	showModalAdd.value = false;
	payload.value = null;
}

function addItemConfirm() {
	if (!payload.value) return;

	store.commit("SET_LOADING", true);

	// const priceStr = payload.value.price.toString().replace(",", ".");
	// const priceCents = Math.round(parseFloat(priceStr) * 100);

	const newPayload = {
		...payload.value,
		// price: priceCents,
	};

	planCreate(newPayload)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				editItem(data);
				toast({ message: message, type: "success" });
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function editItem(data: PlanType) {
	router.push(`/register/plan/${data.plan_id}/edit`);
}

function removeItem(data: PlanType) {
	selected.value = data;
	showModalDel.value = true;
}

function removeItemCancel() {
	showModalDel.value = false;
	selected.value = null;
}

function removeItemConfirm() {
	if (!selected.value) return;

	planDelete(selected.value.plan_id)
		.then((response) => {
			const { success, message } = response;

			fetchData();
			removeItemCancel();
			toast({ message: message, type: success ? "success" : "error" });
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function formatCurrency(value: number, currency: any) {
	const price = Number.parseFloat(value?.toString()).toFixed(2);
	return maskCurrency(price, currency);
}

onMounted(() => {
	fetchData();
});
</script>

<template>
	<article class="register-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="plans"
			></breadcrumb-simple>
		</section>

		<section class="page-content">
			<div class="tab-header">
				<svg-icon icon="plans" size="2rem" />
				{{ t("plan.labels.plans") }}
			</div>

			<div class="tab-content">
				<base-table
					:header="tableHeader"
					:data="tableData"
					:limit="limit"
					:page="page"
					:pages="pages"
					:total="total"
					:searched="searched"
					:loading="loading"
					:options="true"
					:del="true"
					:edit="true"
					@change:page="changePage($event)"
					@searching="searchItem($event)"
					@btn:add="addItem()"
					@btn:edit="editItem($event)"
					@btn:del="removeItem($event)"
				>
					<template #status="{ data }">
						{{ statusOptions[data.status] }}
					</template>

					<template #free="{ data }">
						<template v-if="data.free">
							{{ t("common.labels.yes") }}
						</template>
						<template v-else>
							{{ t("common.labels.not") }}
						</template>
					</template>

					<template #currency="{ data }">
						{{ currencies[data.currency] }}
					</template>

					<template #frequency="{ data }">
						{{ data.frequency }}
						<template v-if="data.frequency > 1">
							{{ t("common.labels.days") }}
						</template>
						<template v-else>
							{{ t("common.labels.day") }}
						</template>
					</template>

					<template #price="{ data }">
						{{ formatCurrency(data.price, data.currency) }}
					</template>
				</base-table>
			</div>
		</section>
	</article>

	<base-modal
		:show="showModalAdd"
		:title="t('plan.modals.titles.plan_add').toUpperCase()"
		:show-button-confirm="true"
		:button-confirm="t('common.labels.save')"
		:button-close="t('common.labels.cancel')"
		@close="addItemCancel()"
		@confirm="addItemConfirm()"
	>
		<template #content>
			<form-plan
				:data="selected"
				@update:data="payload = $event"
				@validate="payloadValid = $event"
			/>
		</template>
	</base-modal>

	<base-confirm
		:show="showModalDel"
		:title="t('plan.modals.titles.plan_remove').toUpperCase()"
		:message="
			t('plan.modals.messages.plan_remove', {
				name: selected?.name?.toUpperCase(),
			})
		"
		@cancel="removeItemCancel()"
		@confirm="removeItemConfirm()"
	/>
</template>

<style lang="scss" scoped>
.register-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 0.5rem;

		.tab-header {
			display: inline-flex;
			gap: 0.5rem;
			font-size: 1.2rem;
			align-items: center;
			padding-inline: 1.25rem;
			padding-block: 0.75rem;
			border-block-end: 1px solid var(--border);
			text-transform: uppercase;
		}

		.tab-content {
			display: flex;
			flex-direction: column;
			padding-inline: 1.5rem;
			padding-block: 1rem;
			gap: 1rem;
		}
	}
}
</style>
